import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchJson } from './adminApi';
import { fmt, kr, typeLabel } from './adminFormat';

const RAMP_TOKENS = ['--ax-seq-1', '--ax-seq-2', '--ax-seq-3', '--ax-seq-4', '--ax-seq-5'];

// A random sample of listings as dots, coloured by price per m² quintile.
const MapTab = ({ onOpenDetail, themeKey }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);
    const fittedRef = useRef(false);
    const openDetailRef = useRef(onOpenDetail);
    openDetailRef.current = onOpenDetail;
    const [points, setPoints] = useState(null);
    const [legend, setLegend] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        fetchJson('/admin/explorer/map')
            .then((data) => active && setPoints(Array.isArray(data) ? data : []))
            .catch((err) => active && setError(err.message));
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return undefined;
        const map = L.map(containerRef.current, { preferCanvas: true }).setView([62.5, 16.5], 5);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 18,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        mapRef.current = map;
        const resize = window.setTimeout(() => map.invalidateSize(), 50);
        return () => {
            window.clearTimeout(resize);
            map.remove();
            mapRef.current = null;
            layerRef.current = null;
            fittedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !points) return;
        const styles = getComputedStyle(containerRef.current);
        const token = (name) => styles.getPropertyValue(name).trim();
        const ramp = RAMP_TOKENS.map(token);
        const muted = token('--ax-muted');
        const surface = token('--ax-surface');
        const sqm = points.map((p) => p[2]).filter(Boolean).sort((a, b) => a - b);
        const quantile = (f) => sqm[Math.floor(f * (sqm.length - 1))] || 0;
        const breaks = [quantile(0.2), quantile(0.4), quantile(0.6), quantile(0.8)];
        const colorFor = (value) => (value ? ramp[breaks.filter((b) => value > b).length] : muted);

        layerRef.current?.remove();
        const layer = L.layerGroup().addTo(map);
        layerRef.current = layer;
        const renderer = L.canvas({ padding: 0.5 });
        points.forEach(([lat, lng, sqmPrice, price, type, label, url]) => {
            const marker = L.circleMarker([lat, lng], {
                renderer, radius: 4, weight: 1, color: surface, fillColor: colorFor(sqmPrice), fillOpacity: 0.9,
            });
            marker.bindPopup(() => {
                const box = document.createElement('div');
                box.className = 'ax-map-popup';
                const title = document.createElement('strong');
                title.textContent = label;
                const facts = document.createElement('div');
                facts.textContent = `${typeLabel(type)} · ${kr(price)} · ${sqmPrice ? `${kr(sqmPrice)}/m²` : 'no kr/m²'}`;
                const details = document.createElement('button');
                details.type = 'button';
                details.className = 'ax-link';
                details.textContent = 'Show images & details';
                details.addEventListener('click', () => openDetailRef.current(url));
                const links = document.createElement('div');
                links.append(details);
                box.append(title, facts, links);
                return box;
            });
            marker.addTo(layer);
        });
        if (points.length && !fittedRef.current) {
            map.fitBounds(L.latLngBounds(points.map((p) => [p[0], p[1]])), { padding: [20, 20] });
            fittedRef.current = true;
        }
        setLegend({ ramp, muted, breaks });
    }, [points, themeKey]);

    return (
        <section className="ax-card ax-map-card">
            <div className="ax-map-head">
                <h3>
                    Listings on the map{' '}
                    <span className="ax-hint">
                        {points ? `random sample of ${fmt(points.length)}, coloured by price per m² (quintiles)` : 'loading…'}
                    </span>
                </h3>
                {legend && (
                    <div className="ax-legend" aria-label="Price per m² legend">
                        {legend.ramp.map((color, index) => (
                            <span key={color + index}>
                                <span className="ax-swatch" style={{ background: color }} />
                                {index < 4 ? `≤ ${fmt(legend.breaks[index])}` : `> ${fmt(legend.breaks[3])}`}
                            </span>
                        ))}
                        <span><span className="ax-swatch" style={{ background: legend.muted }} />no price</span>
                    </div>
                )}
            </div>
            {error && <p className="ax-error ax-map-error">{error}</p>}
            <div ref={containerRef} className="ax-map" />
        </section>
    );
};

export default MapTab;
